import api from 'api';
import type { ApiResponse } from 'api/core/base';
import HttpStatusCode from 'api/core/httpStatusCode';
import config from 'config';
import { DEFAULT_WEEKLY_CAPACITY } from 'constants/account';
import { EMPTY_ARRAY } from 'constants/defaults';
import { errorMessageFactory } from 'factories/api';
import { modifyUserSelectedProjects } from 'factories/user.editor';
import { difference, intersection, map, uniq } from 'lodash-es';
import { batch } from 'react-redux';
import { fetchUserCapacities } from 'redux/modules/capacities';
import {
  showAccountExpiredMessage,
  showSubscriptionNoticeModal,
} from 'redux/modules/subscriptions.actions';
import { getIsDemoManagerPresetActive } from 'redux/modules/tic/tic.demo.selectors';
import { showToast } from 'redux/modules/toasts.actions';
import { isDemoUserId } from 'redux/modules/users-page/demo/usersPage.demo.constants';
import { DEMO_MODE_BLOCKED_MESSAGE } from 'redux/modules/users-page/demo/usersPage.demo.messages';
import { paginationFactory } from 'redux/reusable/pagination';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import {
  getCurrentAccount,
  getHasReachedUserLimit,
  getIsSubscriptionExpiredOrTrialEnded,
} from 'redux/selectors/accounts';
import { getDefaultUserRoleId, getUserRoleById } from 'redux/selectors/roles';
import { getPublicReportFilters } from 'redux/selectors/superreports';
import { getTeamEntities } from 'redux/selectors/teams';
import { getUserPermissions } from 'redux/selectors/user';
import {
  getDraft,
  getProjectRateFilters,
  getProjectsForDropdown,
  getUserEditorPagination,
  getXhrStatus,
} from 'redux/selectors/user.editor';
import { getUserEntities } from 'redux/selectors/users';
import { TrackingActions } from 'tracking/actions';
import type { LightProjectWithRateAndPermission, Project } from 'types/project';
import type { Thunk } from 'types/redux';
import type { Team } from 'types/team';
import type { User } from 'types/user';
import type { UserDraftProject, UsersDraft, UsersPayload } from 'types/users';
import { generateUserAccountPropertiesChangeSet } from 'utils/account';
import { fetchPaginated } from 'utils/api';
import { notNull } from 'utils/typesafe';
import { convertUserToDraft, generateUserDraft } from 'utils/users';

// Lazy import to break circular dependency chain through accounts.actions
const getSaveUserAccountPropertyValues = () =>
  import('./accounts.actions').then(m => m.saveUserAccountPropertyValues);
// Lazy import to break circular dependency: user.editor.actions → projects.actions → forecast.actions → user.editor.actions
const getFetchProject = () => import('./projects.actions').then(m => m.fetchProject);

import { fetchPublicReports, fetchReports } from './superreports.actions';
import type { ToastType } from './toasts.types';
import type { ProjectRateFilters } from './user.editor.types';
import * as types from './user.editor.types';
import { userEditorXhrModule } from './user.editor.types';
import { addUser, fetchUserById, updateUser } from './users.actions';

const { wrapFetching, wrapSaving } = userEditorXhrModule;

export const removeTeamFromUser = types.removeTeamFromUser;
export const removeProjectFromDraft = types.removeProjectAssocationFromDraft;
export const updateDraftProjectRate = types.updateDraftProjectRate;
export const removeAllProjectsFromDraft = types.disassociateAllProjects;
export const updateDraft = types.updateDraft;
export const clearDraft = types.clearDraft;

function initializeUserDraft(user: Partial<UsersDraft>): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    // Create initial draft.
    const defaultRoleId = getDefaultUserRoleId(state);
    const account = getCurrentAccount(state);
    const filter = getPublicReportFilters(state);
    const workspaceWeeklyCapacity = account.weekly_user_capacity ?? DEFAULT_WEEKLY_CAPACITY;
    const defaultWeekdays = account.default_work_days;
    const draft = generateUserDraft(user, {
      defaultRoleId,
      workspaceWeeklyCapacity,
      defaultWeekdays,
    });

    dispatch(types.createDraft(draft));
    dispatch(types.createDraftOriginal(draft));

    // We need fresh reports information in order to show warning:
    //    "Updating this user’s access level may also change the data
    //    available in the live reports they have shared."
    dispatch(fetchReports());
    dispatch(fetchPublicReports(filter));

    // Fetch initial projects
    dispatch(searchProjectsForEditorDropdown());
    dispatch(fetchProjectsForRateList());

    // Some associations need to be side-loaded, as they've been removed
    // from the index/list respones due to processing/size issues
    let teams: Array<Team> = [];
    if (user.id) {
      try {
        // Always fetch team associations to prevent stale cache:
        teams = (await dispatch(wrapFetching(api.getUserTeams(user.id), 'teams'))) || null; // Null collapses the "Teams + " section by default.
        dispatch(
          updateDraft({
            teams,
          })
        );
      } catch (e) {
        // Some workspaces may not have teams, so we don't want to fail here.
      }
    }
    dispatch(types.createDraftOriginal({ ...draft, teams }));
  };
}

import {
  PROJECT_EDITOR_DROPDOWN_KEY,
  PROJECT_EDITOR_RATES_LIST_KEY,
} from './user.editor.constants';

export {
  PROJECT_EDITOR_DROPDOWN_KEY,
  PROJECT_EDITOR_RATES_LIST_KEY,
} from './user.editor.constants';
export function searchProjectsForEditorDropdown(
  query?: string,
  clientId?: number,
  tagIds?: Array<number>
): Thunk<Promise<Array<LightProjectWithRateAndPermission>>> {
  return async (dispatch, getState) => {
    const draft = getDraft(getState());
    if (!draft) {
      return EMPTY_ARRAY;
    }

    const hasQuery = query != null && query !== '';
    const hasTagIds = tagIds != null && tagIds.length > 0;
    const hasQueryOrTagIds = hasQuery || hasTagIds;

    const wantToFetch = hasQuery
      ? undefined
      : hasQueryOrTagIds
        ? config.projects.numProjectSearchResults
        : config.projects.numRecentProjectsToFetch;
    const perPage = config.projects.projectsPerPage;
    // When loading projects for a client, fetch until there are no more pages
    // When searchin, also fetch everything
    // For the initial load, fetch a fixed number of projects
    const pages =
      clientId != null || wantToFetch == null ? undefined : Math.ceil(wantToFetch / perPage);
    const promise = dispatch(
      wrapFetching(
        fetchPaginated(
          { perPage, pages },
          async page =>
            (
              await api.searchForProjects({
                userId: draft.id,
                scope: 'global',
                query,
                clientId,
                filter: 'active',
                rates: true,
                per_page: perPage,
                page,
                labelIds: tagIds,
              })
            ).projects
        ),
        PROJECT_EDITOR_DROPDOWN_KEY
      )
    );

    const projects = (await promise) as Array<LightProjectWithRateAndPermission>;
    dispatch(types.receiveProjectsForDropdown({ projects, tagIds }));
    return projects;
  };
}

export function fetchAddedProjectsCountForClientInEditorDropdown(
  clientIds: Array<number>
): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const draft = getDraft(getState());
    if (!draft) {
      return;
    }
    if (draft.id == null) {
      // The draft has no id, so we can't fetch the count for the user
      return;
    }
    const idsToFetch = clientIds.filter(clientId => draft.addedProjectsByClient[clientId] == null);

    const promises = idsToFetch.map(async clientId => {
      const count = (
        await api.searchForProjects({
          userId: draft.id,
          clientId,
          filter: 'active',
          per_page: 0,
        })
      ).totalCount;
      dispatch(types.receiveProjectsCountForClient({ clientId, count }));
    });
    await dispatch(wrapFetching(Promise.all(promises), PROJECT_EDITOR_DROPDOWN_KEY));
  };
}

function fetchProjectsForRateList(): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const draft = getDraft(state);
    if (draft?.id == null) {
      return;
    }
    const paginationState = getUserEditorPagination(state);
    const filters = getProjectRateFilters(state);

    const { projects } = await dispatch(
      wrapFetching(
        api.searchForProjects({
          userId: draft.id,
          query: filters.queryString || undefined,
          rate_type: filters.rateType || undefined,
          filter: 'active',
          rates: true,
          page: paginationState.page,
          per_page: config.projects.projectsPerPage,
        }),
        PROJECT_EDITOR_RATES_LIST_KEY
      )
    );
    dispatch(
      types.receiveProjectsForRatesList(projects as Array<LightProjectWithRateAndPermission>)
    );
    dispatch(
      types.paginationActions.setParams({
        maybeHasMore: projects.length === config.projects.projectsPerPage,
      })
    );
  };
}

export function setProjectRatesFilter(patch: Partial<ProjectRateFilters>): Thunk<Promise<void>> {
  return async dispatch => {
    dispatch(types.setProjectRateFilters(patch));
    dispatch(types.paginationActions.setParams(paginationFactory()));

    dispatch(fetchProjectsForRateList());
  };
}

export function editUser(user: User): Thunk<Promise<void>> {
  return async dispatch => {
    const freshUser = await dispatch(fetchUserById(user.id));
    return dispatch(initializeUserDraft(freshUser));
  };
}

export function editUserById(userId: number): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    if (isDemoUserId(userId) && getIsDemoManagerPresetActive(state)) {
      dispatch(showToast({ message: DEMO_MODE_BLOCKED_MESSAGE, type: 'info' }));
      return;
    }
    const myUser = getUserEntities(state)[userId];
    dispatch(editUser(myUser));
  };
}

export function createNewUser(user: Partial<UsersDraft> = {}): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    // Not id-scoped like editUserById/deleteUserById/reInviteUser — creating a
    // user isn't about an existing id, it's a change to the real workspace,
    // which demo mode never allows regardless of the preset's specific roster.
    if (getIsDemoManagerPresetActive(state)) {
      dispatch(showToast({ message: DEMO_MODE_BLOCKED_MESSAGE, type: 'info' }));
      return;
    }
    const hasReachedUserLimit = getHasReachedUserLimit(state);
    const hasAccountExpired = getIsSubscriptionExpiredOrTrialEnded(state);

    // Keep track of how many times the user starts creation process:
    TrackingActions.initiateUserAction('New User');

    if (hasReachedUserLimit) {
      dispatch(showSubscriptionNoticeModal('userLimitReached'));
    } else if (hasAccountExpired) {
      dispatch(showAccountExpiredMessage());
    } else {
      dispatch(initializeUserDraft({ ...user, add_to_all_projects: true }));
    }
  };
}

interface SaveUserOptions {
  clearDraft?: boolean;
  addTeamUsersToProjects?: boolean;
  removeTeamUsersFromProjects?: boolean;
}
export function saveUser(
  payload: UsersPayload,
  options: SaveUserOptions = {}
): Thunk<Promise<User>> {
  return async dispatch => {
    let userResponse: ApiResponse<User> | undefined;
    try {
      userResponse = await dispatch(
        wrapSaving(
          api.inviteOrEditUser(payload, {
            add_team_users_to_projects: !!options.addTeamUsersToProjects,
            remove_team_users_from_projects: !!options.removeTeamUsersFromProjects,
          })
        )
      );
      dispatch(fetchUserCapacities());
    } catch (error) {
      let message = `Sorry, something went wrong: ${errorMessageFactory(error)}`;
      let dismissAfter = 10000;
      if (error.cause === 'timeout') {
        message = 'This request timed out; please check to see if the user was added.';
        dismissAfter = 30000;
      }
      dispatch(
        showToast({
          type: 'danger',
          message,
          dismissAfter,
        })
      );
      return Promise.reject(error);
    }

    const user = userResponse.data;

    if (!payload.id) {
      dispatch(addUser(user));
    } else {
      dispatch(updateUser(user));
    }

    if (options.clearDraft) {
      dispatch(types.clearDraft());
      dispatch(types.clearDraftOriginal());
    }

    const notificationState = payload.id ? 'updated' : 'created';
    let toastMessage = `${user.name} ${notificationState}`;
    let toastType: ToastType = 'success';
    if (userResponse.responseCode === HttpStatusCode.ACCEPTED) {
      // HTTP Accepted means they update permissions in a bg job
      // dispatch(types.setShowSavedUserAsyncMessage(true));
      toastMessage += '. Changes to permissions will not be immediately visible.';
      toastType = 'warning';
    }
    dispatch(
      showToast({
        message: toastMessage,
        type: toastType,
      })
    );

    return Promise.resolve(user);
  };
}

export function saveUserDraft(
  draft: UsersDraft,
  options: SaveUserOptions = {}
): Thunk<Promise<User>> {
  return async (dispatch, getState) => {
    const state = getState();
    const originalDraft = state.userEditor.draftOriginal;
    const projectsPayload =
      draft.projects.spec === 'replace'
        ? draft.projects.projects
        : {
            create: draft.projects.add,
            update: draft.projects.update,
            delete: draft.projects.remove.map(project_id => ({ project_id })),
          };
    const payload: UsersPayload = {
      ...draft,
      userAccountProperties: undefined,
      projects: projectsPayload,
      apply_default_rate: draft.apply_default_rate === 'no' ? null : draft.apply_default_rate,
      capacity: {
        weekly_capacity: draft.weekly_capacity,
        work_days: draft.work_days,
      },
    };

    // We only want to send these values up if the rate has changed to avoid setting
    // them unexpectedly, e.g. if we sent the full draft along with update_existing_hours
    // and update_unbilled_only this has the potential to update hour rates
    if (originalDraft?.default_hour_rate === draft.default_hour_rate) {
      delete payload.update_existing_hours;
      delete payload.update_unbilled_only;
      delete payload.hour_rate_effective_from;
      // Also drop the application choice itself: the rate did not change, so a
      // leftover "all projects" from an earlier (reverted) edit must not
      // trigger a reprice — the backend defaults update_existing_hours to true
      // when apply_default_rate is present without it.
      payload.apply_default_rate = null;
    }

    if (originalDraft?.internal_hour_rate === draft.internal_hour_rate) {
      delete payload.update_existing_hours_internal_rate;
      delete payload.update_unbilled_hours_internal_rate;
      delete payload.internal_hour_rate_effective_from;
    }

    // Don't attempt to set admin flag if authenticated user is non-admin:
    if (!state.user.admin) {
      delete payload.admin;
    }

    if (draft.teams && draft.teams.length) {
      payload.team_ids = draft.teams.map(t => t.id || null).filter(notNull);
    } else {
      payload.team_ids = [];
    }
    delete payload.teams;

    const isNewUser = typeof draft.id !== 'number';

    if (isNewUser) {
      payload.properties_attributes = generateUserAccountPropertiesChangeSet(
        [],
        draft.userAccountProperties
      );
    }

    const saveUserAccountPropertyValues = await getSaveUserAccountPropertyValues();
    const [output, _] = await Promise.all([
      dispatch(saveUser(payload, options)),
      dispatch(saveUserAccountPropertyValues(draft)),
    ]);

    return output;
  };
}

export function updateDraftRoleId(role_id: number): Thunk {
  return (dispatch, getState) => {
    const role = getUserRoleById(getState())[role_id];

    if (!role) return;

    // Set scopes & value for the selected role
    const scopesToUser = role?.scopes?.reduce<Record<string, boolean>>((props, scope) => {
      props[scope.name] = scope.default;
      return props;
    }, {});

    dispatch(updateDraft({ role_id, ...scopesToUser }));
  };
}

export function updateDraftDefaultHourRate(newRate: number): Thunk {
  return dispatch => {
    dispatch(types.updateDraftDefaultHourRate(newRate));
  };
}

export function updateDraftInternalCostRate(newRate: number): Thunk {
  return dispatch => {
    dispatch(types.updateDraftInternalCostRate(newRate));
  };
}

export function applyDefaultRateForInvitedUser(): Thunk {
  return dispatch => {
    dispatch(types.applyDefaultRateForChosenProjects());
  };
}

export function addProjectToDraft(id: number): Thunk {
  return async (dispatch, getState) => {
    const state = getState();
    const draft = getDraft(state);
    const loadedProjects = getProjectsForDropdown(state);
    const project = loadedProjects.find(p => p.id === id);

    if (!project || !draft) {
      return;
    }

    const association = generateUserProjectAssociation(project, draft);

    dispatch(types.addProjectAssociationToDraft(association));
  };
}

export function toggleClientOnDraft(clientId: number, filterByTagIds?: Array<number>): Thunk {
  return async (dispatch, getState) => {
    const state = getState();
    const draft = getDraft(state);
    const xhrStatus = getXhrStatus(state);
    const isLoading = xhrStatusUIState(xhrStatus, PROJECT_EDITOR_DROPDOWN_KEY).isLoading;
    if (!draft || isLoading) {
      return;
    }
    const projects = await dispatch(searchProjectsForEditorDropdown(undefined, clientId));
    /** ids for the client which are currently selected */
    let projectIds = map(projects, 'id');
    if (filterByTagIds?.length) {
      projectIds = projectIds.filter(id => filterByTagIds.includes(id));
    }

    const currentlySelected = intersection(
      projectIds,
      modifyUserSelectedProjects(draft.projects, projects)
    );
    batch(() => {
      if (projectIds.length !== currentlySelected.length) {
        // select all
        difference(projectIds, currentlySelected).forEach(id => {
          dispatch(addProjectToDraft(id));
        });
      } else {
        // deselect all
        currentlySelected.forEach(id => {
          dispatch(removeProjectFromDraft(id));
        });
      }
    });
  };
}

export function updateDraftSelectedTeams(selectedTeamIds: Array<number>): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const teams = getTeamEntities(state);
    const selectedTeams = selectedTeamIds.map(teamId => teams[teamId]);
    const draft = getDraft(state);
    if (!draft) {
      return;
    }

    dispatch(updateDraft({ teams: selectedTeams }));

    if (draft.id == null) {
      // We can't do this for existing users, as we don't know which projects the user is added to without searching.
      // We could maybe make a request for the project ids we care about so that we know which projects the user is already added to,
      // and then look at whether we patch or replace the projects, and then add the projects to the correct array.
      // For now, only do this for new users - this function will never be called for existing users as the
      // team dropdown is disabled for existing users.
      dispatch(ensureTeamProjectsAddedToDraft());
    }
  };
}

export function updateDraftDisableAddToAllProjects(): Thunk {
  return dispatch => {
    dispatch(updateDraft({ add_to_all_projects: false }));
    dispatch(ensureTeamProjectsAddedToDraft());
  };
}

function ensureTeamProjectsAddedToDraft(): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const draft = getDraft(state);
    if (!draft || draft.add_to_all_projects) {
      return;
    }

    const teamProjectIds = uniq(draft.teams?.flatMap(team => team.project_ids ?? []));
    teamProjectIds.forEach(projectId => {
      dispatch(addProjectToDraft(projectId));
    });
  };
}

export function generateUserProjectAssociation(
  project: { id: number; rate_type: Project['rate_type'] },
  user: { default_hour_rate: number }
): UserDraftProject {
  let rate: number | null = null;
  if (project.rate_type === 'user') {
    rate = user.default_hour_rate;
  }

  return {
    project_id: project.id,
    hour_rate: rate,
  };
}

export function addUserToProject(userId: number, projectId: number): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const state = getState();
    const permissions = getUserPermissions(state);
    if (!permissions.canUpdate.users) {
      throw Error('Unauthorized: can not update user<->project association');
    }

    const fetchProject = await getFetchProject();
    const [fetchedUser, fetchedProject] = await Promise.all([
      dispatch(fetchUserById(userId)),
      dispatch(fetchProject(projectId)),
    ]);
    if (fetchedProject.users?.find(u => u.user_id === userId)) {
      return;
    }
    await dispatch(
      saveUserDraft({
        ...convertUserToDraft(fetchedUser),
        projects: {
          spec: 'patch',
          add: [generateUserProjectAssociation(fetchedProject, fetchedUser)],
          update: [],
          remove: [],
        },
      })
    );
  };
}
